import {
  type DefaultError, type MutationFunction,
  type MutationOptions, type QueryClient, type QueryKey,
  useMutation,
  useQueryClient
} from '@tanstack/vue-query'

export type InvalidateOn = 'success' | 'error' | 'all' | 'never'
type Options<TData, TVariables> = MutationOptions<TData, DefaultError, TVariables> & { invalidateOn?: InvalidateOn }
type Key<TVariables> = (QueryKey|((input: TVariables) => QueryKey|undefined))

const invalidateKeys = <TVariables>(client: QueryClient, input: TVariables, keys: Key<TVariables>[]) => Promise.all(
  keys.map(
    queryKey => {
      if (Array.isArray(queryKey)) {
        return client.invalidateQueries({ queryKey })
      }

      if (typeof queryKey === 'function') {
        const resolvedKey = queryKey(input)

        if (resolvedKey !== undefined) {
          return client.invalidateQueries({ queryKey: resolvedKey })
        }
      }

      return Promise.resolve()
    }
  )
)

export function useMutationWithInvalidation<TData, TVariables>(options: MutationFunction<TData, TVariables>|Options<TData, TVariables>, keys: Key<TVariables>[] = [], invalidateOn?: InvalidateOn) {
  const client = useQueryClient()
  const optionsObject: Options<TData, TVariables> = typeof options === 'function'
    ? { mutationFn: options, invalidateOn: 'success' }
    : { ...options }
  const effectiveInvalidateOn = invalidateOn ?? optionsObject.invalidateOn ?? 'success'

  return useMutation({
    ...optionsObject,
    async onSuccess(output, input, context) {
      if (effectiveInvalidateOn === 'never') {
        return
      }

      if (effectiveInvalidateOn === 'success' || effectiveInvalidateOn === 'all') {
        await invalidateKeys(client, input, keys)
      }

      return optionsObject.onSuccess?.(output, input, context)
    },
    async onError(error, variables, context) {
      if (effectiveInvalidateOn === 'never') {
        return
      }

      if (effectiveInvalidateOn === 'error' || effectiveInvalidateOn === 'all') {
        await invalidateKeys(client, variables, keys)
      }

      return optionsObject.onError?.(error, variables, context)
    }
  })
}
